import React, { useEffect, useRef, useState, useImperativeHandle, forwardRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";

/**
 * Aria — the UniVicoustic learning companion.
 *
 * An on-brand acoustic-wave entity that floats in the bottom-right corner of the
 * Reading Experience. Idles with a gentle "breath" animation, then reacts to:
 *   • Scroll milestones (25% / 50% / 75% / done) — scripted excited-friend lines.
 *   • Section dwell (>30s on the same heading) — a Claude-generated, content-aware
 *     tip fetched via POST /api/companion/tip.
 *   • Quiz outcomes — cheers on correct, encourages on wrong, celebrates on pass.
 *
 * Personality: excited friend. Short bursts, 1 emoji max, never condescending.
 *
 * Usage:
 *   const ariaRef = useRef();
 *   <LearningCompanion ref={ariaRef} />
 *   ariaRef.current.say("Halfway there!", "cheering");
 */

// 7 emotion presets. The visuals respond to `mood` automatically.
const MOODS = {
  idle:        { coreScale: 1.0,  ringSpeed: 4.0, ringSpread: 1.0, glow: 0.20, coreColor: "#222222", ringColor: "#E53935" },
  thinking:    { coreScale: 0.92, ringSpeed: 5.5, ringSpread: 0.7, glow: 0.30, coreColor: "#222222", ringColor: "#E53935" },
  cheering:    { coreScale: 1.18, ringSpeed: 2.0, ringSpread: 1.35, glow: 0.55, coreColor: "#E53935", ringColor: "#E53935" },
  surprised:   { coreScale: 1.32, ringSpeed: 1.5, ringSpread: 1.5, glow: 0.65, coreColor: "#E53935", ringColor: "#222222" },
  celebrating: { coreScale: 1.25, ringSpeed: 1.4, ringSpread: 1.5, glow: 0.7, coreColor: "#E53935", ringColor: "#E53935" },
  encouraging: { coreScale: 1.05, ringSpeed: 3.2, ringSpread: 1.1, glow: 0.30, coreColor: "#222222", ringColor: "#E53935" },
  listening:   { coreScale: 0.95, ringSpeed: 3.8, ringSpread: 0.85, glow: 0.25, coreColor: "#222222", ringColor: "#222222" },
};

const LearningCompanion = forwardRef(function LearningCompanion({ initialMood = "idle" }, ref) {
  const [mood, setMood] = useState(initialMood);
  const [bubble, setBubble] = useState(null); // { text, key }
  const [minimized, setMinimized] = useState(false);
  // ─── Pulse-alert state ────────────────────────────────────────────────
  // When the reader scrolls into range of an interactive section, the parent
  // calls `nudge()`. We light up a small red dot on Aria's avatar + add an
  // extra-bright pulse ring for 12 s. Aria stays in her home corner — she
  // never floats to the element (per user request).
  const [pulsing, setPulsing] = useState(false);
  const pulseTimer = useRef(null);
  const hideTimer = useRef(null);
  const moodResetTimer = useRef(null);

  // Public API exposed to parent components
  useImperativeHandle(ref, () => ({
    say: (text, nextMood = "cheering", { hideAfter = 7000, holdMoodFor = 4000 } = {}) => {
      // NOTE: the `anchorEl` option was removed (user iter 24). Aria now
      // stays in her home corner regardless of what triggered the speech.
      if (!text) return;
      setBubble({ text, key: Date.now() });
      setMood(nextMood);
      clearTimeout(hideTimer.current);
      clearTimeout(moodResetTimer.current);
      hideTimer.current = setTimeout(() => { setBubble(null); }, hideAfter);
      moodResetTimer.current = setTimeout(() => setMood("idle"), holdMoodFor);
    },
    setMood: (m) => setMood(m in MOODS ? m : "idle"),
    dismiss: () => { setBubble(null); },
    /**
     * Non-blocking visual ping: red dot + extra-bright pulse ring on the
     * avatar for `duration` ms. No speech bubble, no movement. Used when
     * the learner scrolls into range of an interactive section so they
     * notice Aria is "watching", without the disorienting float-out.
     */
    nudge: ({ duration = 12000 } = {}) => {
      setPulsing(true);
      clearTimeout(pulseTimer.current);
      pulseTimer.current = setTimeout(() => setPulsing(false), duration);
    },
  }), []);

  useEffect(() => () => {
    clearTimeout(hideTimer.current);
    clearTimeout(moodResetTimer.current);
    clearTimeout(pulseTimer.current);
  }, []);

  const m = MOODS[mood] || MOODS.idle;

  if (minimized) {
    return (
      <motion.button
        initial={{ opacity: 0, scale: 0.6 }}
        animate={{ opacity: 1, scale: 1 }}
        onClick={() => setMinimized(false)}
        className="fixed bottom-24 right-5 w-10 h-10 rounded-full bg-[#222222] text-white shadow-lg z-50 flex items-center justify-center hover:bg-[#E53935] transition-colors"
        data-testid="aria-restore"
        title="Bring Aria back"
      >
        <span className="text-base">▲</span>
      </motion.button>
    );
  }

  return (
    <motion.div
      className="fixed z-50 pointer-events-none select-none"
      animate={{ top: "auto", left: "auto", right: 20, bottom: 96 }}
      transition={{ type: "spring", stiffness: 240, damping: 28 }}
      data-testid="aria-companion"
    >
      {/* Speech bubble */}
      <AnimatePresence mode="wait">
        {bubble && (
          <motion.div
            key={bubble.key}
            initial={{ opacity: 0, y: 12, scale: 0.85 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.9 }}
            transition={{ type: "spring", stiffness: 320, damping: 24 }}
            className="pointer-events-auto bg-white border border-slate-200 rounded-2xl px-4 py-2.5 shadow-xl max-w-[260px] mb-3 ml-auto relative"
            data-testid="aria-bubble"
          >
            <button
              onClick={() => setBubble(null)}
              className="absolute -top-1.5 -left-1.5 w-5 h-5 rounded-full bg-slate-900 text-white flex items-center justify-center hover:bg-[#E53935] transition-colors"
              data-testid="aria-bubble-dismiss"
              aria-label="Dismiss"
            >
              <X size={11} strokeWidth={2.5}/>
            </button>
            <div className="text-[11px] uppercase tracking-wider text-[#E53935] font-bold mb-0.5">Aria</div>
            <div className="text-sm text-slate-800 leading-snug">{bubble.text}</div>
            {/* Tail pointing down-right toward the avatar */}
            <div className="absolute -bottom-2 right-7 w-3 h-3 bg-white border-r border-b border-slate-200 rotate-45"/>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Avatar — acoustic wave entity */}
      <div className="pointer-events-auto ml-auto relative" style={{ width: 64, height: 64 }} data-testid="aria-avatar-wrap">
        <AriaAvatar mood={m} onClick={() => setMinimized(true)} />
        {/* Pulse-alert dot — appears for ~12 s when nudge() is called.
            Visual-only marker that an interactive section is in view; no
            text, no movement, no float-out. */}
        {pulsing && (
          <motion.div
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: [0, 1.2, 1], opacity: [0, 1, 1] }}
            transition={{ duration: 0.4, ease: "easeOut" }}
            className="absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full bg-[#E53935] ring-2 ring-white shadow-md"
            data-testid="aria-alert-dot"
            title="Interactive content nearby"
          >
            <motion.div
              className="absolute inset-0 rounded-full bg-[#E53935]"
              animate={{ scale: [1, 1.9], opacity: [0.7, 0] }}
              transition={{ duration: 1.4, repeat: Infinity, ease: "easeOut" }}
            />
          </motion.div>
        )}
      </div>
    </motion.div>
  );
});

function AriaAvatar({ mood, onClick }) {
  // Three concentric rings expand outward; the core breathes/spikes per mood.
  const rings = [0, 0.33, 0.66]; // animation-delay phases
  return (
    <motion.div
      className="relative w-full h-full cursor-pointer group"
      onClick={onClick}
      whileHover={{ scale: 1.06 }}
      data-testid="aria-avatar"
      title="Minimize Aria"
    >
      {/* Glow halo */}
      <motion.div
        className="absolute inset-0 rounded-full"
        animate={{ opacity: [mood.glow, mood.glow * 1.5, mood.glow] }}
        transition={{ duration: mood.ringSpeed * 0.6, repeat: Infinity, ease: "easeInOut" }}
        style={{
          background: `radial-gradient(circle, ${mood.ringColor}55 0%, transparent 70%)`,
          filter: "blur(8px)",
        }}
      />

      {/* SVG core + rings */}
      <svg viewBox="0 0 64 64" className="absolute inset-0 w-full h-full overflow-visible">
        {/* Concentric pulse rings */}
        {rings.map((delay, i) => (
          <motion.circle
            key={i}
            cx="32" cy="32"
            r="18"
            fill="none"
            stroke={mood.ringColor}
            strokeWidth="1.5"
            initial={{ scale: 0.4, opacity: 0.85 }}
            animate={{ scale: [0.4, 1.6 * mood.ringSpread], opacity: [0.85, 0] }}
            transition={{
              duration: mood.ringSpeed,
              repeat: Infinity,
              ease: "easeOut",
              delay: delay * mood.ringSpeed,
            }}
            style={{ transformOrigin: "32px 32px" }}
          />
        ))}

        {/* Inner equalizer bars — the "voice" of Aria */}
        <g transform="translate(32, 32)">
          {[-8, -3, 2, 7].map((x, i) => (
            <motion.rect
              key={i}
              x={x - 1}
              width="2"
              fill={mood.coreColor}
              rx="1"
              animate={{
                height: [3, 9 + i * 2, 5, 11 - i * 1.5, 3],
                y: [-1.5, -(9 + i * 2) / 2, -2.5, -(11 - i * 1.5) / 2, -1.5],
              }}
              transition={{
                duration: mood.ringSpeed * 0.45,
                repeat: Infinity,
                ease: "easeInOut",
                delay: i * 0.08,
              }}
            />
          ))}
        </g>

        {/* Core dot — the "heart" */}
        <motion.circle
          cx="32" cy="32"
          r="14"
          fill="none"
          stroke={mood.coreColor}
          strokeWidth="2"
          animate={{ scale: [mood.coreScale, mood.coreScale * 1.08, mood.coreScale] }}
          transition={{ duration: mood.ringSpeed * 0.7, repeat: Infinity, ease: "easeInOut" }}
          style={{ transformOrigin: "32px 32px" }}
        />
      </svg>
    </motion.div>
  );
}

export default LearningCompanion;
